import React, { useMemo } from "react";
import { motion } from "framer-motion";

// Korean alphabet recitation (consonant + ㅏ): 가 나 다 라 마 바 사
const BRAND_WORD = ["가", "나", "다", "라", "마", "바", "사"];

// Multi-color palette for the drifting characters.
const PALETTE = ["#0F172A", "#FF6B9E", "#A78BFA", "#38BDF8", "#BE123C"];

/**
 * KoreanDrift — Korean Hangul characters that gently drift downward.
 * The characters spell out "한강라이프스타일" (Hangang Lifestyle) read
 * left-to-right across the hero — each character anchored to its own column.
 * Designed (not techy) — brand typography, large legible sizes, slow easing,
 * mixed brand colors, fade in/out at top/bottom. No box chrome.
 */
export default function KoreanDrift({ count, seed = 1, dark = false }) {
  const items = useMemo(() => {
    // Seeded-ish PRNG using sin
    const rand = (i) => {
      const x = Math.sin((i + 1) * seed * 9999) * 10000;
      return x - Math.floor(x);
    };
    const total = count || BRAND_WORD.length;
    const chars = Array.from({ length: total }, (_, i) => BRAND_WORD[i % BRAND_WORD.length]);

    return chars.map((char, i) => {
      // Even horizontal spacing so the word reads in order left-to-right
      const slot = (i + 0.5) / total;            // center of each slot
      const jitter = (rand(i + 23) - 0.5) * 2;   // ±2% jitter
      const leftPct = Math.max(4, Math.min(94, slot * 92 + 4 + jitter));

      // Pick a color from the multi-color palette so each character drifts in
      // a distinct brand hue (navy / pink / purple / cyan / mint).
      const paletteIdx = Math.floor(rand(i + 31) * PALETTE.length);
      const color = dark && PALETTE[paletteIdx] === "#0F172A" ? "#FFFFFF" : PALETTE[paletteIdx];

      return {
        char,
        leftPct,
        duration: 38 + rand(i + 7) * 30,           // 38-68s — very slow drift
        delay: -rand(i + 9) * 50,                  // mid-flight already
        size: 38 + Math.floor(rand(i + 13) * 30),  // 38-68px
        weight: rand(i + 17) > 0.55 ? 500 : 400,
        peakOpacity: 0.55 + rand(i + 19) * 0.30,
        color,
      };
    });
  }, [count, seed, dark]);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden select-none" aria-hidden data-testid="korean-drift">
      {items.map((it, i) => (
        <motion.span
          key={i}
          initial={{ y: "-12vh" }}
          animate={{ y: "120vh" }}
          transition={{
            y: { duration: it.duration, delay: it.delay, repeat: Infinity, ease: "linear" },
          }}
          style={{
            position: "absolute",
            left: `${it.leftPct}%`,
            top: 0,
            fontFamily: "Unbounded, sans-serif",
            fontSize: `${it.size}px`,
            fontWeight: it.weight,
            color: it.color,
            letterSpacing: "-0.02em",
            willChange: "transform",
          }}
        >
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, it.peakOpacity, it.peakOpacity, 0] }}
            transition={{
              duration: it.duration,
              delay: it.delay,
              repeat: Infinity,
              ease: "linear",
              times: [0, 0.18, 0.82, 1],
            }}
            style={{ display: "inline-block" }}
          >
            {it.char}
          </motion.span>
        </motion.span>
      ))}
    </div>
  );
}
